import _extends from "@babel/runtime/helpers/extends";
import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _class, _class2, _descriptor, _descriptor2, _AbWithTooltip;
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupPosition } from "abreact/incubator/ui/popup/PopupPosition";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbHSeparator } from "abreact/layout/AbHSeparator";
import { AbVBox } from "abreact/layout/AbVBox";
import { AbReactConstants } from "abreact/service/AbReactConstants";
import { AbListHelper } from "abweb/collection/AbListHelper";
import { ElementUtils } from "abweb/utils/ElementUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { action, computed, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
export const AbWithTooltipContext = /*#__PURE__*/React.createContext({
  inTooltip: false
});

/**
 * The properties for the tooltip shown by AbWithTooltip.
 */

/**
 * The properties for an AbWithTooltip container.
 */

export const TOOLTIP_DEFAULT_OPEN_DELAY = 750;
export const TOOLTIP_CLOSE_DELAY = 250;
export const TOOLTIP_KEYBOARD_CLOSE_DELAY = 75; // It feels better when tabbing around if the tooltips close faster

const areRequestsEqual = (r1, r2) => {
  return r1.id === r2.id && r1.shownBy === r2.shownBy;
};
class TooltipManager {
  constructor(setTopTooltip) {
    // End of array is most recently requested
    _defineProperty(this, "showTooltipRequests", []);
    _defineProperty(this, "requestTooltip", (id, afterMillis, showTooltip) => {
      this.cancelRequest(id);
      const timeoutId = setTimeout(() => {
        const obj = this.getTooltipRequest(id);
        if (!obj) return;
        obj.timeoutCompleted = true;
        showTooltip();
        const shownTooltip = this.showTooltipRequests.at(-1);
        if (shownTooltip && areRequestsEqual(shownTooltip, obj)) {
          // Only update top tooltip if this one is most recent
          this.setTopTooltipWrapper();
        }
      }, afterMillis);
      this.showTooltipRequests.push({
        ...id,
        timeoutId: timeoutId,
        timeoutCompleted: false
      });
    });
    _defineProperty(this, "setTopTooltipWrapper", () => {
      this.setTopTooltip(this.calcTopTooltip());
    });
    _defineProperty(this, "getTooltipRequest", id => {
      return this.showTooltipRequests.find(request => areRequestsEqual(request, id));
    });
    _defineProperty(this, "cancelRequest", id => {
      const requestIndex = this.showTooltipRequests.findIndex(request => areRequestsEqual(request, id));
      if (requestIndex === -1) return;
      const request = this.showTooltipRequests.splice(requestIndex, 1)[0];
      clearTimeout(request.timeoutId);
      this.setTopTooltipWrapper();
    });
    _defineProperty(this, "cancelAll", () => {
      this.showTooltipRequests.forEach(request => clearTimeout(request.timeoutId));
      this.showTooltipRequests = [];
      this.setTopTooltipWrapper();
    });
    _defineProperty(this, "showingKeyboardTooltip", () => {
      return this.calcTopTooltip()?.shownBy === "keyboard";
    });
    _defineProperty(this, "calcTopTooltip", () => {
      return AbListHelper.from(this.showTooltipRequests).findLast(item => item.timeoutCompleted);
    });
    this.setTopTooltip = setTopTooltip;
  }
}
/**
 * A component that renders its children with the ability to show a tooltip.
 *
 * [[include:abreact/public/core/AbButton.md]]
 */
export let AbWithTooltip = observer(_class = (_class2 = (_AbWithTooltip = class AbWithTooltip extends React.Component {
  constructor(abWithTooltipProps) {
    super(abWithTooltipProps);
    _defineProperty(this, "trigger", void 0);
    _defineProperty(this, "mounted", false);
    _initializerDefineProperty(this, "showTooltipKeyboard", _descriptor, this);
    _initializerDefineProperty(this, "showTooltipMouse", _descriptor2, this);
    _defineProperty(this, "mousePosition", void 0);
    _defineProperty(this, "exitTimeout", void 0);
    _defineProperty(this, "uniqueId", TypeUtils.generateUniqueId("ab-with-tooltip"));
    _defineProperty(this, "keyboardId", {
      id: this.uniqueId,
      shownBy: "keyboard"
    });
    _defineProperty(this, "mouseId", {
      id: this.uniqueId,
      shownBy: "mouse"
    });
    _defineProperty(this, "cleanupEventHandler", void 0);
    _defineProperty(this, "renderChildren", tooltip => {
      if (this.props.wrapChildren) {
        const {
          onMouseEnter,
          onMouseLeave,
          onMouseMove,
          onFocusCapture,
          onBlur
        } = this.props;
        return /*#__PURE__*/React.createElement("div", _extends({}, ReactUtils.componentProps("ab-with-tooltip", this.props), {
          onMouseEnter: event => this.onWrappedMouseEnter(event, onMouseEnter),
          onMouseLeave: event => this.onWrappedMouseLeave(event, onMouseLeave),
          onMouseMove: event => this.onWrappedMouseMove(event, onMouseMove),
          onFocusCapture: event => this.onFocusCapture(event, onFocusCapture),
          onBlur: event => this.onBlur(event, onBlur)
        }), this.props.children);
      }
      const {
        onMouseEnter,
        onMouseLeave,
        onMouseMove,
        onFocusCapture,
        onBlur
      } = this.props.children.props;
      const ariaLabelObj = typeof tooltip === "string" ? {
        "aria-label": tooltip
      } : undefined;
      return /*#__PURE__*/React.cloneElement(this.props.children, {
        onMouseEnter: event => this.onWrappedMouseEnter(event, onMouseEnter),
        onMouseLeave: event => this.onWrappedMouseLeave(event, onMouseLeave),
        onMouseMove: event => this.onWrappedMouseMove(event, onMouseMove),
        onFocusCapture: event => this.onFocusCapture(event, onFocusCapture),
        onBlur: event => this.onBlur(event, onBlur),
        ...ariaLabelObj
      });
    });
    _defineProperty(this, "getTooltip", () => {
      if (this.props.clippedTooltip) {
        const clipElement = this.props.clippedTrigger?.current ?? this.trigger;
        if (clipElement && isElementClipped(clipElement)) {
          return this.props.clippedTooltip;
        }
      }
      return this.props.tooltip;
    });
    _defineProperty(this, "shownByActive", shownBy => {
      if (this.showTooltipKeyboard && shownBy === "keyboard") return true;
      if (this.showTooltipMouse && shownBy === "mouse") return true;
      return false;
    });
    _defineProperty(this, "onWrappedMouseEnter", action((event, cb) => {
      cb?.(event);
      // React bubbles events through the react DOM, but we only want tooltips to be connected to children in the HTML DOM.
      if (!event.currentTarget.contains(event.target)) return;
      if (areAllTooltipsDisabled()) return;
      this.trigger = event.currentTarget;
      this.sharedEnter("mouse");
    }));
    _defineProperty(this, "onWrappedMouseLeave", action((event, cb) => {
      cb?.(event);
      this.sharedLeave("mouse");
    }));
    _defineProperty(this, "onWrappedMouseMove", action((event, cb) => {
      cb?.(event);
      // If tooltip is not visible, store the mouse location.
      if (!this.tooltipVisible) {
        this.mousePosition = {
          x: event.clientX,
          y: event.clientY
        };
      }
    }));
    _defineProperty(this, "onFocusCapture", (event, cb) => {
      cb?.(event);
      if (!isFocusEventValid(event)) return;
      if (areAllTooltipsDisabled()) return;
      this.trigger = event.currentTarget;
      this.sharedEnter("keyboard");
    });
    _defineProperty(this, "onBlur", (event, cb) => {
      cb?.(event);
      if (!isFocusEventValid(event)) return;
      this.sharedLeave("keyboard");
    });
    _defineProperty(this, "onTooltipMouseEnter", action(() => {
      this.sharedEnter("mouse");
    }));
    _defineProperty(this, "onTooltipMouseLeave", action(() => {
      this.sharedLeave("mouse");
    }));
    _defineProperty(this, "onBackgroundKeyDown", event => {
      if (event.key !== KeyValue.ESCAPE) return;
      event.stopPropagation();
      event.preventDefault();
      this.close(undefined);
    });
    _defineProperty(this, "close", action(shownBy => {
      const callOnHide = this.tooltipVisible && this.mounted;
      if (shownBy !== "mouse") {
        // undefined | "keyboard"
        AbWithTooltip.TOOLTIP_MANAGER.cancelRequest(this.keyboardId);
        this.showTooltipKeyboard = false;
      }
      if (shownBy !== "keyboard") {
        // undefined | "mouse"
        AbWithTooltip.TOOLTIP_MANAGER.cancelRequest(this.mouseId);
        this.showTooltipMouse = false;
      }
      if (callOnHide) {
        this.props.onHide?.();
      }
    }));
    // On all supported browsers, when moving the mouse from one component to a neighboring component, the mouse exit
    // for the first component is called before the mouse enter on the second.
    _defineProperty(this, "sharedEnter", action(shownByWhat => {
      if (!this.mounted || !this.getTooltip()) return;
      if (this.showTooltipKeyboard && shownByWhat === "keyboard" || this.showTooltipMouse && shownByWhat === "mouse") {
        // If the tooltip is already showing, clear the exit timeout
        clearTimeout(this.exitTimeout);
      } else {
        // Otherwise, start a timeout that will show the tooltip.
        const delay = shownByWhat === "keyboard" && AbWithTooltip.TOOLTIP_MANAGER.showingKeyboardTooltip() ? 0 : this.props.tooltipOpenDelay ?? AbWithTooltip.DEFAULT_OPEN_DELAY;
        AbWithTooltip.TOOLTIP_MANAGER.requestTooltip(shownByWhat === "keyboard" ? this.keyboardId : this.mouseId, delay, action(() => {
          if (!this.mounted || this.trigger && PopupUtils.isElementObscuredByLayer(this.trigger)) {
            return;
          }
          if (shownByWhat === "keyboard") {
            this.showTooltipKeyboard = true;
          } else {
            this.showTooltipMouse = true;
          }
          // clear exit timeout
          clearTimeout(this.exitTimeout);
        }));
      }
    }));
    _defineProperty(this, "sharedLeave", action(leaveByWhat => {
      if (this.shownByActive(leaveByWhat)) {
        const exitFunc = () => {
          if (leaveByWhat === "mouse") {
            this.mousePosition = undefined;
          }
          this.close(leaveByWhat);
        };
        // Start timeout to hide the tooltip.
        this.exitTimeout = setTimeout(exitFunc, leaveByWhat === "keyboard" ? TOOLTIP_KEYBOARD_CLOSE_DELAY : this.props.tooltipCloseDelay ?? AbWithTooltip.DEFAULT_CLOSE_DELAY);
      } else {
        if (leaveByWhat === "mouse") {
          this.mousePosition = undefined;
        }
        AbWithTooltip.TOOLTIP_MANAGER.cancelRequest(leaveByWhat === "keyboard" ? this.keyboardId : this.mouseId);
      }
    }));
    makeObservable(this);
    this.showTooltipKeyboard = false;
    this.showTooltipMouse = false;
  }
  get tooltipVisible() {
    if (this.props.disabled) return false;
    const currentTop = AbWithTooltip.topTooltipIdRef.current;
    if (!currentTop) return false;
    if (this.showTooltipKeyboard && areRequestsEqual(currentTop, this.keyboardId)) return true;
    if (this.showTooltipMouse && areRequestsEqual(currentTop, this.mouseId)) return true;
    return false;
  }
  render() {
    const tooltip = this.getTooltip();
    return /*#__PURE__*/React.createElement(React.Fragment, null, this.renderChildren(tooltip), this.tooltipVisible && tooltip && /*#__PURE__*/React.createElement(AbPopup, _extends({}, this.props.popupProps, {
      resizeBoxRef: ReactUtils.mergeRefs(this.props.popupProps?.resizeBoxRef, element => {
        this.cleanupEventHandler?.();
        if (element) {
          element.element.ownerDocument.addEventListener("keydown", this.onBackgroundKeyDown, {
            capture: true
          });
          this.cleanupEventHandler = () => {
            element.element.ownerDocument.removeEventListener("keydown", this.onBackgroundKeyDown, {
              capture: true
            });
          };
        } else {
          this.cleanupEventHandler = undefined;
        }
      }),
      className: ReactUtils.classNamesString(["ab-tooltip", "ab-font-body-small", this.props.className, this.props.popupProps?.className]),
      style: {
        pointerEvents: undefined,
        ...this.props.popupProps?.style
      },
      allowPropagation: {
        onMouseEnter: true,
        onMouseLeave: true,
        onMouseMove: true
      },
      modal: false,
      closeOnBackgroundClick: !this.props.disableCloseOnBackgroundClick,
      onBackgroundCaptureMouseDown: () => this.close(undefined),
      onClose: () => {
        if (!this.tooltipVisible) return;
        // Only invoke close machinery if booleans need to be updated.
        const showingKeyboardTooltip = AbWithTooltip.TOOLTIP_MANAGER.showingKeyboardTooltip();
        if (this.showTooltipKeyboard && showingKeyboardTooltip) {
          this.close("keyboard");
        } else if (this.showTooltipMouse && !showingKeyboardTooltip) {
          this.close("mouse");
        }
      }
      // Backup positioner happens on focus
      ,
      positioner: this.mousePosition && !AbWithTooltip.TOOLTIP_MANAGER.showingKeyboardTooltip() ? PopupUtils.createMousePositioner(this.mousePosition.x, this.mousePosition.y) : PopupUtils.VERTICAL_FIXED_SIZE_POSITIONER,
      position: PopupPosition.BELOW,
      origin: this.trigger,
      resizable: false,
      draggable: false,
      flushContent: true,
      onMouseEnter: this.onTooltipMouseEnter,
      onMouseLeave: this.onTooltipMouseLeave,
      stayInInitialPosition: this.props.stayInInitialPosition
    }), /*#__PURE__*/React.createElement(AbWithTooltipContext.Provider, {
      value: {
        inTooltip: true
      }
    }, this.props.popupProps?.flushContent ? tooltip : /*#__PURE__*/React.createElement("div", {
      className: "ab-tooltip-content"
    }, tooltip))));
  }
  componentDidMount() {
    this.mounted = true;
  }
  componentWillUnmount() {
    this.mounted = false;
    AbWithTooltip.TOOLTIP_MANAGER.cancelRequest(this.mouseId);
    AbWithTooltip.TOOLTIP_MANAGER.cancelRequest(this.keyboardId);
    clearTimeout(this.exitTimeout);
  }
}, _defineProperty(_AbWithTooltip, "DEFAULT_OPEN_DELAY", TOOLTIP_DEFAULT_OPEN_DELAY), _defineProperty(_AbWithTooltip, "DEFAULT_CLOSE_DELAY", TOOLTIP_CLOSE_DELAY), _defineProperty(_AbWithTooltip, "topTooltipIdRef", observable({
  current: undefined
})), _defineProperty(_AbWithTooltip, "TOOLTIP_MANAGER", new TooltipManager(action(tooltip => _AbWithTooltip.topTooltipIdRef.current = tooltip))), _defineProperty(_AbWithTooltip, "__clearEnterTimeout", () => {
  _AbWithTooltip.TOOLTIP_MANAGER.cancelAll();
}), _AbWithTooltip), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "showTooltipKeyboard", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "showTooltipMouse", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "tooltipVisible", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "tooltipVisible"), _class2.prototype), _class2)) || _class;
const isFocusVisible = event => {
  const productDiv = event.target.ownerDocument.querySelector(".ab-product");
  return productDiv?.classList.contains(AbReactConstants.FOCUS_VISIBLE_CLASS) ?? false;
};
const isFocusEventValid = event => {
  // If I already contain the focus, skip this
  if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return false;
  // If focus is not visible, this behavior isn't active
  if (!isFocusVisible(event)) return false;
  // React bubbles events through the react DOM, but we only want tooltips to be connected to children in the HTML DOM.
  if (!event.currentTarget.contains(event.target)) return false;
  return true;
};
const areAllTooltipsDisabled = () => {
  //Demo givers don't want to show tooltips since they can get in the way, except for when they do want to show tooltips
  //for some particular piece of information. This CSS variable is what the BRE uses to indicate that tooltips should
  //be disabled (and it has to be a CSS variable since we want to allow anyone to override it to disable tooltips).
  //This is the quick and easy solution. Dirk and Mark B. discussed and decided this is "sleazy but acceptable."
  return ElementUtils.getCss("--ab-show-tooltips-override-private", "enabled") !== "enabled";
};
const isElementClipped = element => {
  if (element.clientWidth === 0 || element.clientHeight === 0) {
    return false; // If the clip element is invisible, not useful to report as clipped. Firefox reports clientWidth/height as 0 on inline elements.
  }
  return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
};
(function (_AbWithTooltip2) {
  const makeClippedProps = _AbWithTooltip2.makeClippedProps = (tooltip, label) => {
    const getClippedTooltip = () => {
      if (!label) {
        return undefined;
      }
      if (!tooltip) {
        return label;
      }
      return /*#__PURE__*/React.createElement(AbVBox, null, /*#__PURE__*/React.createElement("div", null, label), /*#__PURE__*/React.createElement(AbHSeparator, null), /*#__PURE__*/React.createElement("div", null, tooltip));
    };
    return {
      tooltip: tooltip,
      clippedTooltip: getClippedTooltip()
    };
  };
})(AbWithTooltip || (AbWithTooltip = {}));
//# sourceMappingURL=AbWithTooltip.js.map
